import Chart from 'chart.js/auto';
import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { icon } from '../shared/components/icons';
import { infoIcon } from '../shared/components/InfoIcon';
import { paginate, filterBar, pager } from '../shared/lib/tableControls';
import { PortfolioMetricsChart } from '../shared/components/PortfolioMetricsChart';

interface PaceFin {
  n: number; nights: number; gross: number;
  commissionable: number; bottom_line: number; mgmt_fee: number;
  total_company: number; owner_net: number;
}

interface PaceWindow {
  key: string;
  ty: PaceFin;
  stly: PaceFin;
  delta_pct: number | null;
}

interface LeaderboardRow {
  listing_id: string;
  property_name: string;
  n_res: number;
  gross: number;
  mgmt_fee: number;
  take_rate: number;
}

interface OverviewData {
  kpi: {
    total_gross: number;
    total_mgmt_fee: number;
    total_reservations: number;
    active_listings: number;
    take_rate: number;
    expected_take_rate?: number;
    next_90d_count: number;
    next_90d_gross: number;
    next_90d_mgmt: number;
    pickup_30d_count: number;
    pickup_30d_mgmt: number;
  };
  bookings_24h: {
    n_bookings: number;
    nights_booked: number;
    gross: number;
    mgmt_fee: number;
    gross_in_month: number;
    gross_future: number;
  };
  pace: PaceWindow[];
  leaderboard: LeaderboardRow[];
}

interface PropertyDetail {
  listing: {
    id: string;
    name: string;
    short_name: string;
    mgmt_fee_percent: number;
    pms_listing_id: string;
  };
  kpi: {
    n_reservations: number;
    total_gross: number;
    total_mgmt_fee: number;
    total_nights: number;
    avg_nightly_rate: number;
    take_rate: number;
    channels: string[];
  };
  monthly: { month: string; n_res: number; nights: number; gross: number; mgmt_fee: number }[];
  reservations: {
    id: string;
    hostaway_id: string;
    check_in: string;
    check_out: string;
    nights: number;
    guest_name: string;
    guests: number;
    channel: string;
    gross: number;
    cleaning_fee: number;
    mgmt_fee: number;
    status: string;
  }[];
}

export class PortfolioPage {
  private container: HTMLElement;
  private data: OverviewData | null = null;
  private paceChart:   Chart | null = null;
  // Pace chart series toggles (Notes-4 p7/p9: one graph, add/hide the
  // financial KPIs + projections; the summary table reacts too).
  private paceMetrics: Set<string> = new Set(['gross']);
  private paceShowStly = true;
  private paceShowProjected = false;
  private hub: any = null; // /api/daily-hub payload (projections per month)

  private loadPacePrefs(): void {
    try {
      const raw = localStorage.getItem('pace_chart_series');
      if (!raw) return;
      const p = JSON.parse(raw);
      if (Array.isArray(p.metrics) && p.metrics.length) this.paceMetrics = new Set(p.metrics);
      if (typeof p.stly === 'boolean') this.paceShowStly = p.stly;
      if (typeof p.projected === 'boolean') this.paceShowProjected = p.projected;
    } catch (_) { /* defaults */ }
  }
  private savePacePrefs(): void {
    try { localStorage.setItem('pace_chart_series', JSON.stringify({ metrics: [...this.paceMetrics], stly: this.paceShowStly, projected: this.paceShowProjected })); } catch (_) { /* noop */ }
  }
  private drawerChart: Chart | null = null;
  private assignmentByListing = new Map<string, any>();
  private metricsChart: PortfolioMetricsChart | null = null;

  // Property Leaderboard — client-side filter + pagination state.
  private lbSearch = '';
  private lbTakeRate = 'all'; // all | under10 | 10to20 | over20
  private lbPage = 1;
  private readonly lbPageSize = 25;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  // Guard a percentage for display — never render NaN/Infinity.
  private static pct(n: number | null | undefined, decimals = 1): string {
    return Number.isFinite(n as number) ? `${(n as number).toFixed(decimals)}%` : '—';
  }
  // Guard a money value for display — never render $NaN/$undefined.
  private static money(n: number | null | undefined): string {
    return Number.isFinite(n as number) ? Formatters.formatCurrency(n) : '—';
  }

  async render(): Promise<void> {
    // Portfolio Overview is financial (revenue, mgmt-fee, owner-net, take-rate).
    // It mounts in both the Analytics hub and the Portfolio hub, which stay open
    // to all office roles — so the gate lives here, not just in the nav. Admin +
    // Director only; ground/remote managers keep the operational pages.
    let _role = ''; try { _role = sessionStorage.getItem('userRole') || ''; } catch (_) { /* */ }
    if (_role !== 'admin' && _role !== 'director') {
      this.container.innerHTML = `<div class="page-content"><div style="max-width:520px;margin:64px auto;text-align:center;padding:32px;border:1px solid var(--border);border-radius:16px;background:var(--card);">
        <div style="font-size:34px;margin-bottom:10px;">🔒</div>
        <h2 style="margin:0 0 8px;font-size:18px;">Financials are restricted</h2>
        <p style="color:var(--text-muted);font-size:13.5px;line-height:1.55;margin:0;">The Portfolio financial overview — revenue, management fee, owner-net and take-rate — is limited to Admin and Director roles.</p>
      </div></div>`;
      return;
    }
    this.container.innerHTML = `
      <style>
        /* Portfolio page — overflow-safe responsive refinements */
        .pf-24h-grid { grid-template-columns: repeat(6, minmax(0, 1fr)); }
        /* Leaderboard / revenue tables share the standard .data-table look but keep
           a sensible min-width so they scroll (inside .u-table-wrap) instead of
           crushing on narrow viewports. */
        #portfolio-leaderboard-table { min-width: 640px; }
        #portfolio-revmetrics-table { min-width: 720px; }
        /* Drawer KPI row — overflow-safe tracks (was bare 1fr). */
        .pf-drawer-kpi-row { grid-template-columns: repeat(5, minmax(0, 1fr)); }
        @media (max-width: 900px) {
          .pf-24h-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        }
        @media (max-width: 640px) {
          .pf-24h-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .pf-24h-cell:nth-child(2n) { border-right: none; }
          .pf-drawer-kpi-row { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
      </style>
      <div class="page-content">

        <div class="dash-head">
          <h1>Portfolio Overview</h1>
          <p>Top-line portfolio health — revenue, occupancy, and how you stack up against the market. Use it for the 30-second read; drill into Triage or the property workup for the why.</p>
        </div>

        <div id="portfolio-metrics-chart"></div>

        <div class="kpi-section-label">Portfolio · all future stays</div>
        <div id="portfolio-kpi-grid" class="kpi-grid">
          <div class="pf-kpi primary">${LoadingSpinner.render()}</div>
        </div>

        <div id="pf-goal-compliance" style="margin:-6px 0 20px;"></div>

        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h">
            <div>
              <h3>Last 24 Hours</h3>
              <p>New bookings by creation time</p>
            </div>
          </div>
          <div id="portfolio-24h">${LoadingSpinner.render()}</div>
        </div>

        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h">
            <div>
              <h3>Pace vs Same Time Last Year</h3>
              <p>Top value = on the books now &nbsp;·&nbsp; STLY = what was on the books at this exact point last year (booked by ${new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toLocaleDateString()}) &nbsp;·&nbsp; engine-true: gross → commissionable → bottom line → total company rev</p>
            </div>
          </div>
          <div id="pace-series-chips" style="display:flex; gap:6px; flex-wrap:wrap; padding:12px 20px 0;"></div>
          <div style="padding:10px 20px 0;">
            <canvas id="pace-chart" height="90"></canvas>
          </div>
          <div class="pf-pace-wrap">
            <table id="portfolio-pace-table" class="pf-pace-table">
              <tbody><tr><td colspan="6" style="padding:24px;">${LoadingSpinner.render()}</td></tr></tbody>
            </table>
          </div>
        </div>

        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h">
            <div>
              <h3>Property Leaderboard</h3>
              <p>Next 365 days · active bookings · sorted by management revenue · click a row to drill in</p>
            </div>
          </div>
          <div id="portfolio-leaderboard-controls" style="padding:12px 20px 0;"></div>
          <div class="u-table-wrap" style="max-height:340px; overflow-y:auto;">
            <table id="portfolio-leaderboard-table" class="data-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th class="num">Res</th>
                  <th class="num">Gross</th>
                  <th class="num">Mgmt Rev</th>
                  <th class="num">Take Rate ${infoIcon('blended_rate')}</th>
                  <th style="width:24px;"></th>
                </tr>
              </thead>
              <tbody id="portfolio-leaderboard">
                <tr><td colspan="6" style="padding:24px;">${LoadingSpinner.render()}</td></tr>
              </tbody>
            </table>
          </div>
          <div id="portfolio-leaderboard-pager" style="padding:0 20px;"></div>
        </div>

        <!-- Unit economics (moved from Money → Overview, V8 2B): current month
             on the books + trailing-12-month context. -->
        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h">
            <div>
              <h3>Unit Economics</h3>
              <p>Current month on the books · ADR / Occupancy / RevPAR across active listings · vs the same month last year where the trailing 12 months cover it</p>
            </div>
          </div>
          <div id="pf-unit-econ" style="padding:14px 20px;">${LoadingSpinner.render()}</div>
        </div>

        <!-- Per-property performance (absorbed from the Performance tab, V8 2A):
             occupancy / ADR / RevPAR / MPI with MARKET comparisons per property. -->
        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h">
            <div>
              <h3>Per-Property Performance</h3>
              <p>Occupancy · ADR (with the market's ADR beside it) · RevPAR · MPI · vs STLY — 30-day windows, sortable, with portfolio averages</p>
            </div>
          </div>
          <div id="pf-performance-host" style="padding:0 8px;">${LoadingSpinner.render()}</div>
        </div>

        <div class="pq-card" style="margin-top:18px;">
          <div class="pq-card-h">
            <div>
              <h3>Revenue Breakdown</h3>
              <p>Engine-true per property · <span id="portfolio-revmetrics-window">this year</span> · Gross → Commissionable → Mgmt Fee → Total Company → Owner Net. Formulas are edited in ONE place — Portfolio → Property Settings.</p>
            </div>
          </div>
          <div class="u-table-wrap">
            <table id="portfolio-revmetrics-table" class="data-table">
              <thead><tr>
                ${['Property','Formula','Gross','Commissionable','Mgmt Fee','Total Company','Owner Net'].map((h,i)=>`<th class="${i<=1?'':'num'}">${h}</th>`).join('')}
              </tr></thead>
              <tbody id="portfolio-revmetrics"><tr><td colspan="7" style="padding:24px;">${LoadingSpinner.render()}</td></tr></tbody>
              <tfoot id="portfolio-revmetrics-foot"></tfoot>
            </table>
          </div>
        </div>

      </div>

      <div id="property-drawer-overlay" class="pf-overlay"></div>
      <aside id="property-drawer" class="pf-drawer">
        <div id="property-drawer-content"></div>
      </aside>
    `;

    // Portfolio Metrics combo chart (moved here from Reservations — it's a
    // portfolio financial view). Owns its own fetch/controls/Chart lifecycle.
    this.metricsChart?.destroy();
    this.metricsChart = new PortfolioMetricsChart();
    const chartHost = document.getElementById('portfolio-metrics-chart');
    if (chartHost) void this.metricsChart.render(chartHost);

    await this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const res = await fetch('/api/portfolio-overview');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      this.data = await res.json();
      this.loadPacePrefs();
      this.renderKpi();
      this.renderGoalCompliance();
      this.render24h();
      this.renderPace();
      this.renderLeaderboard();
    } catch (err) {
      console.error('Portfolio overview failed:', err);
      const el = document.getElementById('portfolio-kpi-grid');
      if (el) el.innerHTML = '<div class="empty-state" style="grid-column:1/-1">Failed to load portfolio data.</div>';
    }
    this.loadRevenueMetrics();
    this.renderUnitEconomics();
    this.mountPerformance();
  }

  // Unit economics (V8 2B — moved from Money → Overview): ADR/Occ/RevPAR for
  // the current month on the books, LY beside it when the window covers it.
  private async renderUnitEconomics(): Promise<void> {
    const el = document.getElementById('pf-unit-econ');
    if (!el) return;
    try {
      // /api/portfolio/occ-trend returns a bare array of trailing months:
      // [{ month 'YYYY-MM', occ (integer %), revenue, nights }].
      const series: any[] = await fetch('/api/portfolio/occ-trend').then(r => r.ok ? r.json() : []);
      if (!Array.isArray(series) || !series.length) { el.innerHTML = '<div class="empty-state">No occupancy trend data.</div>'; return; }
      const cur = series[series.length - 1];
      const lyKey = `${Number(cur.month.slice(0, 4)) - 1}${cur.month.slice(4)}`;
      const ly = series.find((s: any) => s.month === lyKey) || null;
      const adrOf = (s: any) => (Number(s.nights) > 0 ? Number(s.revenue) / Number(s.nights) : null);
      const revparOf = (s: any) => { const a = adrOf(s); return a != null && Number.isFinite(Number(s.occ)) ? a * (Number(s.occ) / 100) : null; };
      const money = (v: any) => (v != null && Number.isFinite(Number(v))) ? '$' + Math.round(Number(v)).toLocaleString() : '—';
      const pct = (v: any) => Number.isFinite(Number(v)) ? Math.round(Number(v)) + '%' : '—';
      const cell = (label: string, val: string, lyVal: string | null) => `
        <div class="kpi-card">
          <div class="kpi-card-top"><span class="kpi-card-label">${label}</span></div>
          <div class="kpi-card-value">${val}</div>
          <div class="kpi-card-cap">${cur.month} on the books${lyVal ? ` · LY ${lyVal}` : ' · LY outside the 12-mo window'}</div>
        </div>`;
      el.innerHTML = `<div class="kpi-grid cols-3">
        ${cell('ADR', money(adrOf(cur)), ly ? money(adrOf(ly)) : null)}
        ${cell('Occupancy', pct(cur.occ), ly ? pct(ly.occ) : null)}
        ${cell('RevPAR', money(revparOf(cur)), ly ? money(revparOf(ly)) : null)}
      </div>`;
    } catch {
      el.innerHTML = '<div class="empty-state">Unit economics unavailable.</div>';
    }
  }

  // Per-property performance (V8 2A — the Performance tab absorbed whole):
  // the SAME page class renders into a section here; one implementation.
  private async mountPerformance(): Promise<void> {
    const host = document.getElementById('pf-performance-host');
    if (!host) return;
    try {
      const { AnalyticsPerformancePage } = await import('./AnalyticsPerformancePage');
      await new AnalyticsPerformancePage(host).render();
    } catch (err) {
      console.error('performance section failed:', err);
      host.innerHTML = '<div class="empty-state">Performance section unavailable.</div>';
    }
  }

  private async loadRevenueMetrics(): Promise<void> {
    // Window: the global month picker when set, else the current year. This is
    // the table the operator reads when they change the month — it must follow.
    const g = (window as any).globalDateRange as { from: string; to: string; month?: string } | undefined;
    const y = new Date().getFullYear();
    const from = g?.from || `${y}-01-01`;
    const to = g?.to || `${y}-12-31`;
    const windowEl = document.getElementById('portfolio-revmetrics-window');
    if (windowEl) windowEl.textContent = g?.month ? `month of ${g.month}` : `${y} (pick a month above to narrow)`;
    const tbody = document.getElementById('portfolio-revmetrics');
    try {
      const [res, assignRes] = await Promise.all([
        fetch(`/api/portfolio/revenue-metrics?start_date=${from}&end_date=${to}`),
        fetch('/api/formula-assignments'),
      ]);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const assignments: any[] = assignRes.ok ? await assignRes.json() : [];
      this.assignmentByListing = new Map(assignments.filter(a => a.assignment).map(a => [String(a.listing_id), a.assignment]));
      if (!tbody) return;
      const money = (v: number) => Number.isFinite(v) ? '$' + Math.round(v).toLocaleString() : '—';
      const rows = (data.rows || []);
      if (!rows.length) { tbody.innerHTML = '<tr><td colspan="7" class="empty-state" style="padding:24px;">No revenue in range.</td></tr>'; return; }
      // Formula is DISPLAY-ONLY here (V8 #1): per-property config has ONE home —
      // Portfolio → Property Settings. The cell shows the effective formula and
      // links there instead of offering a second editing surface.
      const formulaCell = (r: any) => {
        const assigned = this.assignmentByListing.get(String(r.id));
        const label = assigned?.formula_code || `default (${(r.formula_code || '20%').replace('WCH_', '')})`;
        return `<span style="font-size:11px;">${Formatters.escapeHtml(label)}</span>
          <a class="rev-formula-edit" data-goto-ps="1" style="font-size:10px; color:var(--info); cursor:pointer; margin-left:4px;" title="Edit in Property Settings">edit</a>`;
      };
      tbody.innerHTML = rows.map((r: any) => `
        <tr>
          <td style="font-weight:500;">${Formatters.escapeHtml(r.name || r.id)}</td>
          <td>${formulaCell(r)}</td>
          <td class="num">${money(r.gross)}</td>
          <td class="num">${money(r.commissionable)}</td>
          <td class="num" style="color:var(--warning-text);">${money(r.mgmt_fee)}</td>
          <td class="num" style="font-weight:600; color:var(--success-text);">${money(r.total_company)}</td>
          <td class="num text-muted">${money(r.owner_net)}</td>
        </tr>`).join('');
      const t = data.totals || {};
      const foot = document.getElementById('portfolio-revmetrics-foot');
      if (foot) foot.innerHTML = `<tr style="background:var(--bg-subtle); font-weight:700;">
        <td>TOTAL</td><td></td>
        <td class="num">${money(t.gross)}</td>
        <td class="num">${money(t.commissionable)}</td>
        <td class="num" style="color:var(--warning-text);">${money(t.mgmt_fee)}</td>
        <td class="num" style="color:var(--success-text);">${money(t.total_company)}</td>
        <td class="num">${money(t.owner_net)}</td></tr>`;

      // "edit" links jump to the ONE formula-editing surface (V8 #1).
      if (tbody && !(tbody as any)._psLinkBound) {
        (tbody as any)._psLinkBound = true;
        tbody.addEventListener('click', (e) => {
          if (!(e.target as HTMLElement).closest('[data-goto-ps]')) return;
          sessionStorage.setItem('hub_tab', 'settings');
          document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'portfolio' }));
        });
      }
    } catch (err) {
      console.error('revenue-metrics failed:', err);
      if (tbody) tbody.innerHTML = '<tr><td colspan="7" class="empty-state" style="padding:24px;">Failed to load revenue breakdown.</td></tr>';
    }
  }

  // FB#4 — the graph that says something: how much of the book is beating,
  // meeting (within 5%), or below its revenue expectation (AirROI projection
  // at each property's percentile, projected to year-end).
  private async renderGoalCompliance(): Promise<void> {
    const el = document.getElementById('pf-goal-compliance');
    if (!el) return;
    try {
      const r = await fetch('/api/daily-hub');
      if (!r.ok) return;
      const h = await r.json();
      this.hub = h;
      if (this.paceShowProjected && this.data) this.renderPaceChart(this.data.pace);
      const d = h?.on_pace?.dist;
      const total = h?.on_pace?.total || 0;
      if (!d || !total) return;
      const seg = (n: number, bg: string, fg: string, label: string) => n
        ? `<div style="flex:${n}; background:${bg}; color:${fg}; font-size:11px; font-weight:600; padding:5px 0; text-align:center; white-space:nowrap; overflow:hidden;" class="mono num" title="${label}">${n}</div>`
        : '';
      el.innerHTML = `
        <div style="border:1px solid var(--border); border-radius:8px; padding:12px 16px; background:#fff;">
          <div style="display:flex; align-items:baseline; gap:10px; flex-wrap:wrap;">
            <span style="font-size:10px; font-weight:500; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Goal compliance · vs AirROI projection at each property's percentile</span>
            <span style="flex:1;"></span>
            <span style="font-size:11px; color:var(--text-faint, #9ca3af);">projected finish: <span style="color:var(--success);">beating &gt;105%</span> · meeting 95–105% · <span style="color:var(--danger);">below &lt;95%</span> — drill in on Today</span>
          </div>
          <div style="display:flex; gap:2px; border-radius:6px; overflow:hidden; margin-top:8px;">
            ${seg(d.beating, 'var(--success-soft)', 'var(--success-text)', 'beating expectations')}
            ${seg(d.meeting, 'var(--info-soft)', 'var(--info-text)', 'meeting (within 5%)')}
            ${seg(d.below, 'var(--danger-soft)', 'var(--danger-text)', 'below expectations')}
          </div>
          <div class="mono num" style="font-size:11px; color:var(--text-muted); margin-top:6px;">${d.beating} beating · ${d.meeting} meeting · ${d.below} below — of ${total} properties with a projection</div>
        </div>`;
    } catch (_) { /* optional band */ }
  }

  private renderKpi(): void {
    if (!this.data) return;
    const { kpi } = this.data;
    const el = document.getElementById('portfolio-kpi-grid');
    if (!el) return;

    const takeMeets = Number.isFinite(kpi.expected_take_rate)
      ? (Number(kpi.take_rate) >= Number(kpi.expected_take_rate) ? 'tone-good' : 'tone-bad')
      : '';

    el.innerHTML = `
      <div class="kpi-card tone-good">
        <div class="kpi-card-top"><span class="kpi-card-label">Mgmt revenue · all future stays</span><span class="kpi-card-ic">${icon('wallet', 17)}</span></div>
        <div class="kpi-card-value">${PortfolioPage.money(kpi.total_mgmt_fee)}</div>
        <div class="kpi-card-cap">our cut of every confirmed stay from today forward &nbsp;·&nbsp; ${Number.isFinite(kpi.total_reservations) ? kpi.total_reservations : '—'} res &nbsp;·&nbsp; ${Number.isFinite(kpi.active_listings) ? kpi.active_listings : '—'} props</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-card-top"><span class="kpi-card-label">Gross (guest paid) · all future stays</span><span class="kpi-card-ic">${icon('trending', 17)}</span></div>
        <div class="kpi-card-value">${PortfolioPage.money(kpi.total_gross)}</div>
        <div class="kpi-card-cap">every confirmed stay from today forward</div>
      </div>
      <div class="kpi-card">
        <div class="kpi-card-top"><span class="kpi-card-label">Mgmt revenue · next 90 days</span><span class="kpi-card-ic">${icon('calendar', 17)}</span></div>
        <div class="kpi-card-value">${PortfolioPage.money(kpi.next_90d_mgmt)}</div>
        <div class="kpi-card-cap">${Number.isFinite(kpi.next_90d_count) ? kpi.next_90d_count : '—'} reservations &nbsp;·&nbsp; ${PortfolioPage.money(kpi.next_90d_gross)} gross</div>
      </div>
      <div class="kpi-card ${takeMeets}">
        <div class="kpi-card-top"><span class="kpi-card-label">Blended Take Rate ${infoIcon('blended_rate')}</span><span class="kpi-card-ic">${icon('target', 17)}</span></div>
        <div class="kpi-card-value">${PortfolioPage.pct(kpi.take_rate)}</div>
        <div class="kpi-card-cap">${Number.isFinite(kpi.expected_take_rate) ? `contract-implied ${PortfolioPage.pct(kpi.expected_take_rate)} → blended ${PortfolioPage.pct(kpi.take_rate)} — ${Number(kpi.take_rate) >= Number(kpi.expected_take_rate) ? 'fees we keep 100% raise it' : 'pass-through fees in gross (cleaning etc.) dilute it'}` : 'mgmt ÷ gross across all future bookings'}</div>
      </div>
    `;
  }

  private render24h(): void {
    if (!this.data) return;
    const b = this.data.bookings_24h;
    const el = document.getElementById('portfolio-24h');
    if (!el) return;

    if (b.n_bookings === 0) {
      el.innerHTML = '<div class="empty-state" style="padding:32px;">No new bookings in the last 24 hours.</div>';
      return;
    }

    el.innerHTML = `
      <div class="pf-24h-grid">
        <div class="pf-24h-cell">
          <div class="pf-24h-label">Bookings</div>
          <div class="pf-24h-value">${Number.isFinite(b.n_bookings) ? b.n_bookings : '—'}</div>
        </div>
        <div class="pf-24h-cell">
          <div class="pf-24h-label">Nights</div>
          <div class="pf-24h-value">${Number.isFinite(b.nights_booked) ? b.nights_booked : '—'}</div>
        </div>
        <div class="pf-24h-cell">
          <div class="pf-24h-label">Gross Revenue</div>
          <div class="pf-24h-value">${PortfolioPage.money(b.gross)}</div>
        </div>
        <div class="pf-24h-cell primary">
          <div class="pf-24h-label">Mgmt Revenue</div>
          <div class="pf-24h-value">${PortfolioPage.money(b.mgmt_fee)}</div>
        </div>
        <div class="pf-24h-cell">
          <div class="pf-24h-label">Gross · In-Month</div>
          <div class="pf-24h-value">${PortfolioPage.money(b.gross_in_month)}</div>
        </div>
        <div class="pf-24h-cell">
          <div class="pf-24h-label">Gross · Future</div>
          <div class="pf-24h-value">${PortfolioPage.money(b.gross_future)}</div>
        </div>
      </div>
    `;
  }

  private paceLabel(key: string): string {
    const t = new Date();
    const y = t.getFullYear();
    const m = t.getMonth();
    const name = (mo: number) => new Date(y, mo, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
    switch (key) {
      case 'mtd':     return `MTD<br><small style="font-weight:400;">${name(m)}</small>`;
      case 'curr_mo': return `${name(m)}<br><small style="font-weight:400;">Full month</small>`;
      case 'plus1':   return name(m + 1);
      case 'plus2':   return name(m + 2);
      case 'plus3':   return name(m + 3);
      default: return key;
    }
  }

  private renderPace(): void {
    if (!this.data) return;
    const pace = this.data.pace;
    const table = document.getElementById('portfolio-pace-table');
    if (!table) return;

    const col = (content: string) => `<th style="text-align:right; min-width:120px;">${content}</th>`;
    const cell = (v: string, muted = false) =>
      `<td style="text-align:right; ${muted ? 'color:var(--text-muted);' : ''}">${v}</td>`;
    const num = (n: number) => Number.isFinite(n) ? String(n) : '—';

    const chipCell = (pct: number | null) => {
      if (pct === null || !Number.isFinite(pct)) return `<td style="text-align:right;"><span class="pf-chip pf-chip-nil">—</span></td>`;
      const cls = pct >= 0 ? 'pf-chip-up' : 'pf-chip-down';
      const arrow = pct >= 0 ? '▲' : '▼';
      return `<td style="text-align:right;"><span class="pf-chip ${cls}">${arrow} ${Math.abs(pct).toFixed(1)}%</span></td>`;
    };

    const row = (label: string, cells: string, cls = '') =>
      `<tr class="${cls}"><td>${label}</td>${cells}</tr>`;

    table.innerHTML = `
      <thead>
        <tr>
          <th style="text-align:left; min-width:140px; padding:10px 16px;"></th>
          ${pace.map(w => col(this.paceLabel(w.key))).join('')}
        </tr>
      </thead>
      <tbody>
        ${row('Bookings',        pace.map(w => this.tyStlyCell(num(w.ty.n), num(w.stly.n))).join(''))}
        ${row('Nights',          pace.map(w => this.tyStlyCell(num(w.ty.nights), num(w.stly.nights))).join(''))}
        ${this.paceMetrics.has('gross') ? row('Gross (guest paid)', pace.map(w => this.tyStlyCell(PortfolioPage.money(w.ty.gross), PortfolioPage.money(w.stly.gross))).join(''), 'pf-divider') : ''}
        ${this.paceMetrics.has('commissionable') ? row('Commissionable', pace.map(w => this.tyStlyCell(PortfolioPage.money(w.ty.commissionable), PortfolioPage.money(w.stly.commissionable))).join('')) : ''}
        ${this.paceMetrics.has('bottom_line') ? row('Bottom line', pace.map(w => this.tyStlyCell(PortfolioPage.money(w.ty.bottom_line), PortfolioPage.money(w.stly.bottom_line))).join('')) : ''}
        ${this.paceMetrics.has('mgmt_fee') ? row('Mgmt fee', pace.map(w => this.tyStlyCell(PortfolioPage.money(w.ty.mgmt_fee), PortfolioPage.money(w.stly.mgmt_fee))).join('')) : ''}
        ${this.paceMetrics.has('total_company') ? row('Total company rev', pace.map(w => this.tyStlyCell(PortfolioPage.money(w.ty.total_company), PortfolioPage.money(w.stly.total_company))).join(''), 'pf-divider') : ''}
        ${row('Δ% co rev',       pace.map(w => chipCell(w.delta_pct)).join(''), 'pf-pace-row')}
      </tbody>
    `;
    this.renderPaceSeriesChips();
    this.renderPaceChart(pace);
  }

  // TY value with its STLY (on-the-books at the same point last year) beneath.
  private tyStlyCell(ty: string, stly: string): string {
    return `<td style="text-align:right;">
      <div style="font-weight:600; color:var(--text);">${ty}</div>
      <div style="font-size:11px; color:var(--text-muted); font-family:var(--font-mono, monospace);">${stly} STLY</div>
    </td>`;
  }

  private paceChartLabel(key: string): string | string[] {
    const t = new Date();
    const m = t.getMonth();
    const name = (mo: number) => new Date(t.getFullYear(), mo, 1).toLocaleString('default', { month: 'short', year: '2-digit' });
    switch (key) {
      case 'mtd':     return ['MTD', name(m)];
      case 'curr_mo': return [name(m), 'Full mo.'];
      case 'plus1':   return name(m + 1);
      case 'plus2':   return name(m + 2);
      case 'plus3':   return name(m + 3);
      default:        return key;
    }
  }

  private static PACE_SERIES: { key: string; label: string; color: string }[] = [
    { key: 'gross',          label: 'Gross (guest paid)', color: '31,41,55' },
    { key: 'commissionable', label: 'Commissionable',     color: '37,99,235' },
    { key: 'bottom_line',    label: 'Bottom line',        color: '124,58,237' },
    { key: 'mgmt_fee',       label: 'Mgmt fee',           color: '180,83,9' },
    { key: 'total_company',  label: 'Total company rev',  color: '4,120,87' },
  ];

  private renderPaceSeriesChips(): void {
    const el = document.getElementById('pace-series-chips');
    if (!el) return;
    const chip = (key: string, label: string, on: boolean, kind: string) =>
      `<button data-pace-chip="${kind}:${key}" style="font-size:11px; padding:3px 10px; border-radius:999px; cursor:pointer; border:1px solid ${on ? 'var(--text)' : 'var(--border)'}; background:${on ? 'var(--text)' : '#fff'}; color:${on ? '#fff' : 'var(--text-muted)'};">${on ? '✓ ' : ''}${label}</button>`;
    el.innerHTML = PortfolioPage.PACE_SERIES.map(s => chip(s.key, s.label, this.paceMetrics.has(s.key), 'metric')).join('')
      + `<span style="width:8px;"></span>`
      + chip('stly', 'STLY pairs', this.paceShowStly, 'flag')
      + chip('projected', 'Projected (AirROI)', this.paceShowProjected, 'flag');
    if (!(el as any)._bound) {
      (el as any)._bound = true;
      el.addEventListener('click', (e) => {
        const b = (e.target as HTMLElement).closest<HTMLElement>('[data-pace-chip]');
        if (!b) return;
        const [kind, key] = b.dataset.paceChip!.split(':');
        if (kind === 'metric') {
          if (this.paceMetrics.has(key)) { if (this.paceMetrics.size > 1) this.paceMetrics.delete(key); }
          else this.paceMetrics.add(key);
        } else if (key === 'stly') this.paceShowStly = !this.paceShowStly;
        else if (key === 'projected') this.paceShowProjected = !this.paceShowProjected;
        this.savePacePrefs();
        this.renderPace();
      });
    }
  }

  // Map each pace window to its calendar month so the AirROI projection from
  // the daily hub can overlay; the MTD window gets the day-prorated target.
  private paceProjectedFor(pace: PaceWindow[]): (number | null)[] {
    const months = this.hub?.months;
    if (!Array.isArray(months)) return pace.map(() => null);
    const cur = new Date().getMonth();
    return pace.map((w, i) => {
      const m = i === 0 ? cur : cur + (i - 1);                     // MTD shares the current month
      return m <= 11 ? (months[m]?.projected ?? null) : null;
    });
  }

  private renderPaceChart(pace: PaceWindow[]): void {
    const canvas = document.getElementById('pace-chart') as HTMLCanvasElement;
    if (!canvas) return;
    if (this.paceChart) { this.paceChart.destroy(); this.paceChart = null; }
    const labels = pace.map(w => this.paceChartLabel(w.key));
    const datasets: any[] = [];
    for (const s of PortfolioPage.PACE_SERIES) {
      if (!this.paceMetrics.has(s.key)) continue;
      datasets.push({
        label: `TY ${s.label}`, data: pace.map(w => (w.ty as any)[s.key]),
        backgroundColor: `rgba(${s.color},0.85)`, borderRadius: 3, barPercentage: 0.5, categoryPercentage: 0.75,
      });
      if (this.paceShowStly) datasets.push({
        label: `STLY ${s.label}`, data: pace.map(w => (w.stly as any)[s.key]),
        backgroundColor: `rgba(${s.color},0.28)`, borderRadius: 3, barPercentage: 0.5, categoryPercentage: 0.75,
      });
    }
    if (this.paceShowProjected) datasets.push({
      type: 'line', label: 'Projected (AirROI)', data: this.paceProjectedFor(pace),
      borderColor: '#f59e0b', borderWidth: 2, borderDash: [6, 4], pointRadius: 3, pointBackgroundColor: '#f59e0b', fill: false,
    });
    this.paceChart = new Chart(canvas, {
      type: 'bar',
      data: { labels, datasets },
      options: {
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'top', labels: { font: { size: 11 }, boxWidth: 12, padding: 14 } },
          tooltip: {
            callbacks: {
              label: ctx => ` ${ctx.dataset.label}: ${Formatters.formatCurrency(ctx.raw as number)}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: v => '$' + Formatters.formatNumber(v as number), font: { size: 10 } },
            grid: { color: '#f1f5f9' },
          },
          x: { ticks: { font: { size: 11 } }, grid: { display: false } },
        },
      },
    });
  }

  private renderLeaderboard(): void {
    if (!this.data) return;
    const tbody = document.getElementById('portfolio-leaderboard');
    const controls = document.getElementById('portfolio-leaderboard-controls');
    const pagerEl = document.getElementById('portfolio-leaderboard-pager');
    if (!tbody) return;

    const lb = this.data.leaderboard;
    if (!lb.length) {
      if (controls) controls.innerHTML = '';
      if (pagerEl) pagerEl.innerHTML = '';
      tbody.innerHTML = '<tr><td colspan="6" class="empty-state">No future bookings found.</td></tr>';
      return;
    }

    // Rank + bar scale are computed over the FULL (already-sorted) leaderboard so
    // #1 stays gold and the bar fill stays comparable regardless of filter/page.
    const maxMgmt = Math.max(...lb.map(p => p.mgmt_fee), 0);
    const barPct = (v: number) => {
      const p = maxMgmt > 0 ? (v / maxMgmt) * 100 : 0;
      return Number.isFinite(p) ? p.toFixed(1) : '0';
    };
    const rankCls = (i: number) => i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : '';
    const rankByListing = new Map<string, number>();
    lb.forEach((p, i) => rankByListing.set(String(p.listing_id), i));

    const search = this.lbSearch.trim().toLowerCase();
    const filtered = lb.filter(p => {
      if (search && !String(p.property_name || '').toLowerCase().includes(search)) return false;
      const tr = p.take_rate;
      if (this.lbTakeRate === 'under10') return Number.isFinite(tr) && tr < 10;
      if (this.lbTakeRate === '10to20') return Number.isFinite(tr) && tr >= 10 && tr <= 20;
      if (this.lbTakeRate === 'over20')  return Number.isFinite(tr) && tr > 20;
      return true;
    });

    const pg = paginate(filtered, this.lbPage, this.lbPageSize);
    this.lbPage = pg.page;

    if (controls) {
      controls.innerHTML = filterBar({
        searchId: 'pf-lb-search',
        searchValue: this.lbSearch,
        searchPlaceholder: 'Search properties…',
        selects: [{
          id: 'pf-lb-takerate',
          value: this.lbTakeRate,
          options: [
            { value: 'all',     label: 'All take rates' },
            { value: 'under10', label: 'Under 10%' },
            { value: '10to20',  label: '10–20%' },
            { value: 'over20',  label: 'Over 20%' },
          ],
        }],
        countLabel: `${pg.total} propert${pg.total === 1 ? 'y' : 'ies'}`,
      });
    }

    if (!pg.total) {
      tbody.innerHTML = '<tr><td colspan="6" class="empty-state">No properties match the current filters.</td></tr>';
    } else {
      tbody.innerHTML = pg.items.map(p => {
        const i = rankByListing.get(String(p.listing_id)) ?? 0;
        return `
        <tr class="pf-lb-row" data-listing-id="${p.listing_id}">
          <td>
            <div style="display:flex; align-items:center; gap:10px; min-width:0;">
              <span class="pf-rank ${rankCls(i)}">${i + 1}</span>
              <div style="min-width:0;">
                <div class="u-truncate" style="font-weight:500; max-width:280px;">
                  ${Formatters.escapeHtml(p.property_name)}
                </div>
                <div class="pf-lb-bar-wrap" style="width:120px;">
                  <div class="pf-lb-bar-fill" style="width:${barPct(p.mgmt_fee)}%;"></div>
                </div>
              </div>
            </div>
          </td>
          <td class="num">${Number.isFinite(p.n_res) ? p.n_res : '—'}</td>
          <td class="num text-muted">${PortfolioPage.money(p.gross)}</td>
          <td class="num" style="font-weight:600;">${PortfolioPage.money(p.mgmt_fee)}</td>
          <td class="num">${PortfolioPage.pct(p.take_rate)}</td>
          <td class="num pf-lb-chevron">›</td>
        </tr>
      `;
      }).join('');
    }

    if (pagerEl) {
      pagerEl.innerHTML = pager({
        idPrefix: 'pf-lb',
        page: pg.page,
        pages: pg.pages,
        total: pg.total,
        pageSize: this.lbPageSize,
      });
    }

    tbody.querySelectorAll<HTMLTableRowElement>('.pf-lb-row').forEach(row => {
      row.addEventListener('click', () => {
        const id = row.dataset.listingId;
        if (id) this.openPropertyDrawer(id);
      });
    });

    this.bindLeaderboardControls();
  }

  private bindLeaderboardControls(): void {
    const search = document.getElementById('pf-lb-search') as HTMLInputElement | null;
    search?.addEventListener('input', () => {
      this.lbSearch = search.value;
      this.lbPage = 1;
      this.renderLeaderboard();
      const next = document.getElementById('pf-lb-search') as HTMLInputElement | null;
      if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
    });
    const takeRate = document.getElementById('pf-lb-takerate') as HTMLSelectElement | null;
    takeRate?.addEventListener('change', () => {
      this.lbTakeRate = takeRate.value;
      this.lbPage = 1;
      this.renderLeaderboard();
    });
    document.getElementById('pf-lb-prev')?.addEventListener('click', () => { this.lbPage--; this.renderLeaderboard(); });
    document.getElementById('pf-lb-next')?.addEventListener('click', () => { this.lbPage++; this.renderLeaderboard(); });
  }

  private async openPropertyDrawer(listingId: string): Promise<void> {
    const overlay = document.getElementById('property-drawer-overlay');
    const drawer  = document.getElementById('property-drawer');
    const content = document.getElementById('property-drawer-content');
    if (!overlay || !drawer || !content) return;

    content.innerHTML = `<div style="padding:24px;">${LoadingSpinner.render()}</div>`;
    overlay.classList.add('open');
    drawer.classList.add('open');

    const close = () => {
      overlay.classList.remove('open');
      drawer.classList.remove('open');
    };
    overlay.onclick = close;

    try {
      const res = await fetch(`/api/property/${listingId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d: PropertyDetail = await res.json();
      content.innerHTML = this.renderPropertyDetail(d);
      content.querySelector<HTMLButtonElement>('#drawer-close-btn')?.addEventListener('click', () => {
        if (this.drawerChart) { this.drawerChart.destroy(); this.drawerChart = null; }
        close();
      });
      this.renderDrawerChart(d.monthly);
    } catch {
      content.innerHTML = `<div style="padding:24px;" class="empty-state">Failed to load property data.</div>`;
    }
  }

  private renderDrawerChart(monthly: PropertyDetail['monthly']): void {
    const canvas = document.getElementById('drawer-monthly-chart') as HTMLCanvasElement;
    if (!canvas || !monthly.length) return;
    if (this.drawerChart) { this.drawerChart.destroy(); this.drawerChart = null; }
    const labels = monthly.map(m =>
      new Date(m.month + '-02').toLocaleString('default', { month: 'short', year: '2-digit' })
    );
    this.drawerChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: 'Gross Revenue',
            data: monthly.map(m => m.gross),
            backgroundColor: 'rgba(59,130,246,0.75)',
            borderRadius: 4,
            barPercentage: 0.5,
            categoryPercentage: 0.8,
          },
          {
            label: 'Mgmt Revenue',
            data: monthly.map(m => m.mgmt_fee),
            backgroundColor: 'rgba(34,197,94,0.75)',
            borderRadius: 4,
            barPercentage: 0.5,
            categoryPercentage: 0.8,
          },
        ],
      },
      options: {
        responsive: true,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'top', labels: { font: { size: 11 }, boxWidth: 12, padding: 12 } },
          tooltip: {
            callbacks: {
              label: ctx => ` ${ctx.dataset.label}: ${Formatters.formatCurrency(ctx.raw as number)}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: v => '$' + Formatters.formatNumber(v as number), font: { size: 10 } },
            grid: { color: '#f1f5f9' },
          },
          x: { ticks: { font: { size: 10 } }, grid: { display: false } },
        },
      },
    });
  }

  private channelPip(ch: string): string {
    const key = ch.toLowerCase();
    const cls = key.includes('airbnb') ? 'pf-ch-airbnb'
      : key.includes('vrbo') ? 'pf-ch-vrbo'
      : key.includes('homeaway') ? 'pf-ch-homeaway'
      : key === 'direct' ? 'pf-ch-direct'
      : 'pf-ch-other';
    const label = ch.replace('Official', '').replace('official', '');
    return `<span class="pf-channel-pip ${cls}">${label}</span>`;
  }

  private statusDot(s: string): string {
    const color = s === 'confirmed' ? '#22c55e' : s === 'modified' ? '#f59e0b' : '#94a3b8';
    return `<span class="pf-status-dot" style="background:${color};"></span>`;
  }

  private guestAvatar(name: string): string {
    const initials = name.split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('');
    return `<span class="pf-guest-avatar">${initials || '?'}</span>`;
  }

  private renderPropertyDetail(d: PropertyDetail): string {
    const { listing, kpi, monthly, reservations } = d;
    const name = listing.short_name || listing.name;
    const maxGross = Math.max(...monthly.map(m => m.gross), 1);

    const monthlyRows = monthly.map(m => {
      const label = new Date(m.month + '-02').toLocaleString('default', { month: 'short', year: '2-digit' });
      const barPct = (m.gross / maxGross * 100).toFixed(0);
      return `
        <tr style="border-bottom:1px solid #f1f5f9;">
          <td style="padding:9px 12px; font-size:13px; font-weight:500;">${label}</td>
          <td style="padding:9px 12px; text-align:center; font-size:13px;">${m.n_res}</td>
          <td style="padding:9px 12px; text-align:center; font-size:13px;">${m.nights}</td>
          <td style="padding:9px 12px; text-align:right; font-size:13px;">
            ${PortfolioPage.money(m.gross)}
            <div class="pf-mo-bar-wrap" style="display:block; margin-top:4px;">
              <div class="pf-mo-bar-fill" style="width:${barPct}%;"></div>
            </div>
          </td>
          <td style="padding:9px 12px; text-align:right; font-size:13px; font-weight:600;">${PortfolioPage.money(m.mgmt_fee)}</td>
        </tr>`;
    }).join('');

    const resRows = reservations.map(r => `
      <tr style="border-bottom:1px solid #f1f5f9;">
        <td style="padding:10px 12px; white-space:nowrap; font-size:13px;">${r.check_in}</td>
        <td style="padding:10px 12px; white-space:nowrap; font-size:13px;">${r.check_out}</td>
        <td style="padding:10px 12px; text-align:center; font-size:13px;">${r.nights}</td>
        <td style="padding:10px 12px; font-size:13px;">
          ${this.guestAvatar(r.guest_name)}${Formatters.escapeHtml(r.guest_name)}
        </td>
        <td style="padding:10px 12px; text-align:center; font-size:12px;">${r.guests}</td>
        <td style="padding:10px 12px;">${this.channelPip(r.channel)}</td>
        <td style="padding:10px 12px; text-align:right; font-size:13px;">${PortfolioPage.money(r.gross)}</td>
        <td style="padding:10px 12px; text-align:right; font-size:12px; color:var(--text-muted);">${PortfolioPage.money(r.cleaning_fee)}</td>
        <td style="padding:10px 12px; text-align:right; font-size:13px; font-weight:600;">${PortfolioPage.money(r.mgmt_fee)}</td>
        <td style="padding:10px 12px; font-size:12px;">${this.statusDot(r.status)}${r.status}</td>
      </tr>`).join('');

    return `
      <div class="pf-drawer-header">
        <div>
          <div class="pf-drawer-title">${Formatters.escapeHtml(name)}</div>
          <div class="pf-drawer-sub">
            <span>${PortfolioPage.pct(listing.mgmt_fee_percent, 0)} fee</span>
            ${kpi.channels.map(c => this.channelPip(c)).join('')}
          </div>
        </div>
        <button id="drawer-close-btn" class="pf-close-btn" aria-label="Close">✕</button>
      </div>

      <div class="pf-drawer-body">
        <div class="pf-drawer-kpi-row">
          <div class="pf-drawer-kpi primary">
            <div class="pf-drawer-kpi-label">Mgmt Revenue</div>
            <div class="pf-drawer-kpi-value">${PortfolioPage.money(kpi.total_mgmt_fee)}</div>
          </div>
          <div class="pf-drawer-kpi">
            <div class="pf-drawer-kpi-label">Gross Revenue</div>
            <div class="pf-drawer-kpi-value">${PortfolioPage.money(kpi.total_gross)}</div>
          </div>
          <div class="pf-drawer-kpi">
            <div class="pf-drawer-kpi-label">Reservations</div>
            <div class="pf-drawer-kpi-value">${Number.isFinite(kpi.n_reservations) ? kpi.n_reservations : '—'}</div>
          </div>
          <div class="pf-drawer-kpi">
            <div class="pf-drawer-kpi-label">Total Nights</div>
            <div class="pf-drawer-kpi-value">${Number.isFinite(kpi.total_nights) ? kpi.total_nights : '—'}</div>
          </div>
          <div class="pf-drawer-kpi">
            <div class="pf-drawer-kpi-label">Avg Nightly ${infoIcon('adr')}</div>
            <div class="pf-drawer-kpi-value">${PortfolioPage.money(kpi.avg_nightly_rate)}</div>
          </div>
        </div>

        <div class="pf-drawer-section">
          <div class="pf-drawer-section-title">Monthly Revenue — Last 12 Months + Upcoming</div>
          <canvas id="drawer-monthly-chart" height="110" style="display:block; margin-bottom:20px;"></canvas>
          <div class="pf-drawer-section-title" style="margin-top:4px;">Monthly Breakdown</div>
          <div style="border:1px solid var(--border); border-radius:10px; overflow:hidden;">
            <table style="width:100%; border-collapse:collapse;">
              <thead>
                <tr style="background:#f8fafc;">
                  <th style="padding:9px 12px; text-align:left; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Month</th>
                  <th style="padding:9px 12px; text-align:center; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Res</th>
                  <th style="padding:9px 12px; text-align:center; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Nights</th>
                  <th style="padding:9px 12px; text-align:right; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Gross</th>
                  <th style="padding:9px 12px; text-align:right; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Mgmt Rev</th>
                </tr>
              </thead>
              <tbody>${monthlyRows || '<tr><td colspan="5" class="empty-state">No data</td></tr>'}</tbody>
            </table>
          </div>
        </div>

        <div class="pf-drawer-section">
          <div class="pf-drawer-section-title">Upcoming Reservations (${reservations.length})</div>
          <div style="border:1px solid var(--border); border-radius:10px; overflow:hidden;">
            <div style="overflow-x:auto;">
              <table style="min-width:820px; width:100%; border-collapse:collapse;">
                <thead>
                  <tr style="background:#f8fafc;">
                    <th style="padding:9px 12px; text-align:left; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Check-in</th>
                    <th style="padding:9px 12px; text-align:left; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Check-out</th>
                    <th style="padding:9px 12px; text-align:center; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Nts</th>
                    <th style="padding:9px 12px; text-align:left; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Guest</th>
                    <th style="padding:9px 12px; text-align:center; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Guests</th>
                    <th style="padding:9px 12px; text-align:left; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Channel</th>
                    <th style="padding:9px 12px; text-align:right; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Gross</th>
                    <th style="padding:9px 12px; text-align:right; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Cleaning</th>
                    <th style="padding:9px 12px; text-align:right; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Mgmt</th>
                    <th style="padding:9px 12px; text-align:left; font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Status</th>
                  </tr>
                </thead>
                <tbody>${resRows || '<tr><td colspan="10" class="empty-state">No upcoming reservations.</td></tr>'}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
